import type Flexmonster from 'flexmonster';

export type StormPivotValue = string | number | null;
export type StormPivotRow = Record<string, StormPivotValue>;

export interface StormPivotMappingField {
    type: 'date string' | 'number' | 'string';
    format?: string;
    interval?: string;
}

export interface StormPivotOptions extends Flexmonster.Options {
    expandAll: boolean;
}

export type StormPivotMapping = Record<string, StormPivotMappingField>;

export enum StormProperties {
    basin = 'Basin',
    stormId = 'Storm Id',
    stormDate = 'Storm Date',
    excluded = 'Excluded',
    peakRainfall = 'Peak Rainfall',
    totalRainfall = 'Total Rainfall',
    precompType = 'Precompensation Type',
    precompAdjustment = 'Precompensation Adjustment',
    stormDuration = 'Storm Duration',
    stormRainfall = 'Storm Rainfall',
    stormGrossQPeak = 'Storm Gross Q Peak',
    stormGrossQVol = 'Storm Gross Q Volume',
    stormGross2Peak = 'Storm Gross II Peak',
    stormGross2Vol = 'Storm Gross II Volume',
    stormNet2Peak = 'Storm Net II Peak',
    stormNet2Vol = 'Storm Net II Volume',
    eventDuration = 'Event Duration',
    eventRainfall = 'Event Rainfall',
    eventGrossQPeak = 'Event Gross Q Peak',
    eventGrossQVol = 'Event Gross Q Volume',
    eventGross2Peak = 'Event Gross II Peak',
    eventGross2Vol = 'Event Gross II Volume',
    eventNet2Peak = 'Event Net II Peak',
    eventNet2Vol = 'Event Net II Volume',
    rec1GrossQPeak = 'Recovery 1 Gross Q Peak',
    rec1GrossQVol = 'Recovery 1 Gross Q Volume',
    rec1Gross2Peak = 'Recovery 1 Gross II Peak',
    rec1Gross2Vol = 'Recovery 1 Gross II Volume',
    rec1Net2Peak = 'Recovery 1 Net II Peak',
    rec1Net2Vol = 'Recovery 1 Net II Volume',
    rec2GrossQPeak = 'Recovery 2 Gross Q Peak',
    rec2GrossQVol = 'Recovery 2 Gross Q Volume',
    rec2Gross2Peak = 'Recovery 2 Gross II Peak',
    rec2Gross2Vol = 'Recovery 2 Gross II Volume',
    rec2Net2Peak = 'Recovery 2 Net II Peak',
    rec2Net2Vol = 'Recovery 2 Net II Volume',
    area = 'Area',
    sewerLength = 'Sewer Length',
    eventNet2RVal = 'Event Net II R-value',
    stormNet2RVal = 'Storm Net II R-value',
    grossEventPeakFactor = 'Gross Event Peaking Factor',
    eventNet2 = 'Event Net II',
    stormNet2 = 'Storm Net II'
}

const STRING_KEYS = [StormProperties.basin];
const STORM_DATE_KEYS = [StormProperties.stormDate];

const STORM_SLICE_ROWS = [StormProperties.basin];
const STORM_SLICE_COLUMNS = [StormProperties.stormDate];

// there columns should be assigned to the end of the report when downloading
export const STORM_END_COLUMNS: string[] = [
    StormProperties.area, StormProperties.sewerLength, StormProperties.eventNet2RVal, 
    StormProperties.stormNet2RVal, StormProperties.eventNet2, StormProperties.stormNet2,
];

export const STORM_SLICE_MEASURES = [
    StormProperties.peakRainfall, StormProperties.precompType, StormProperties.precompAdjustment, StormProperties.stormDuration,
    StormProperties.stormGrossQPeak, StormProperties.stormGrossQVol, StormProperties.stormGross2Peak, StormProperties.stormGross2Vol,
    StormProperties.eventDuration, StormProperties.eventRainfall, StormProperties.eventGrossQPeak, StormProperties.grossEventPeakFactor,
    StormProperties.eventGrossQVol, StormProperties.eventGross2Peak, StormProperties.eventGross2Vol, StormProperties.eventNet2Peak, StormProperties.eventNet2Vol,
    StormProperties.rec1GrossQPeak, StormProperties.rec1GrossQVol, StormProperties.rec1Gross2Peak, StormProperties.rec1Gross2Vol,
    StormProperties.rec1Net2Peak, StormProperties.rec1Net2Vol, StormProperties.rec2GrossQPeak, StormProperties.rec2GrossQVol,
    StormProperties.rec2Gross2Peak, StormProperties.rec2Gross2Vol, StormProperties.rec2Net2Peak, StormProperties.rec2Net2Vol,
    StormProperties.area, StormProperties.sewerLength, StormProperties.eventNet2RVal, StormProperties.eventNet2, StormProperties.stormNet2
];

export const STORM_CHART_MEASURES = [
    StormProperties.totalRainfall, StormProperties.stormRainfall, StormProperties.stormGrossQPeak, StormProperties.stormGrossQVol,
    StormProperties.stormGross2Peak, StormProperties.stormGross2Vol, StormProperties.stormNet2Peak, StormProperties.stormNet2Vol,
    StormProperties.eventRainfall, StormProperties.eventGrossQPeak, StormProperties.grossEventPeakFactor, StormProperties.eventGrossQVol,
    StormProperties.eventGross2Peak, StormProperties.eventGross2Vol, StormProperties.eventNet2Peak, StormProperties.eventNet2Vol,
    StormProperties.eventNet2RVal, StormProperties.stormNet2RVal, StormProperties.eventNet2, StormProperties.stormNet2
];

export const getKeyByProp = (storm: StormPivotRow, prop: StormProperties): string => {
    const stormKeys = Object.keys(storm);

    return stormKeys.slice().reverse().find((key) => key.includes(prop)) ?? prop;
};

export const getStormPivotSlice = (storm: StormPivotRow): Flexmonster.Slice => {
    return {
        rows: STORM_SLICE_ROWS.map((prop) => ({ uniqueName: getKeyByProp(storm, prop) })),
        columns: STORM_SLICE_COLUMNS.map((prop) => ({ uniqueName: getKeyByProp(storm, prop) })),
        measures: STORM_CHART_MEASURES.map((prop) => ({ uniqueName: getKeyByProp(storm, prop) })),
    };
};

export const getStormPivotOptions = (
    datePattern: string,
    dateTimePattern: string,
    chartMeasure: string,
): StormPivotOptions => {
    return {
        showAggregationLabels: false,
        showAggregations: false,
        datePattern,
        dateTimePattern,
        expandAll: true,
        viewType: 'charts',
        grid: {
            type: 'flat',
            showGrandTotals: 'off',
            showTotals: 'off',
        },
        chart: {
            multipleMeasures: true,
            activeMeasure: { uniqueName: chartMeasure },
        },
    };
};

export const createPivotMapping = (data: StormPivotRow, dateTimePattern: string): StormPivotMapping => {
    const mapping: StormPivotMapping = {};

    Object.keys(data).forEach((key) => {
        const value = data[key];
        const isString = STRING_KEYS.some((property) => key === property);
        const isDate = STORM_DATE_KEYS.some((property) => key === property);
        const type: StormPivotMappingField['type'] = typeof value === 'number' && !isString ? 'number' : 'string';

        mapping[key] = isDate
            ? {
                  type: 'date string',
                  format: dateTimePattern,
                  interval: '30s',
              }
            : { type };
    });

    return mapping;
};

export const csvToJSON = (text: string, quoteChar = '"', delimiter = ','): StormPivotRow[] => {
    const lines = text.split('\n');
    const headerLine = lines.shift() ?? '';
    const heads = headerLine.split(',');
    const regex = new RegExp(`\\s*(${quoteChar})?(.*?)\\1\\s*(?:${delimiter}|$)`, 'gs');
    const match = (line: string): string[] =>
        [...line.matchAll(regex)].map((matchResult) => matchResult[2]).slice(0, -1);

    return lines.map((line) => {
        const row: StormPivotRow = {};

        match(line).forEach((currentValue, index) => {
            let value: StormPivotValue;

            if (index === 0 || currentValue.includes('/')) {
                value = currentValue;
            } else if (currentValue.length === 0) {
                value = null;
            } else if (isNaN(Number(currentValue.replace(/,/g, '')))) {
                value = currentValue;
            } else {
                value = parseFloat(currentValue.replace(/,/g, ''));
            }

            const key = heads[index] ?? `{${index}}`;
            row[key] = value;
        });

        return row;
    });
};

const stormDatePattern =
    /^(\d{1,4})\/(\d{1,2})\/(\d{1,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i;

const padDatePart = (value: number, length = 2): string => value.toString().padStart(length, '0');

export const normalizeStormDate = (value: StormPivotValue, dateFormat: string): StormPivotValue => {
    if (typeof value !== 'string') {
        return value;
    }

    const match = stormDatePattern.exec(value.trim());
    if (!match) {
        return value;
    }

    const firstDatePart = Number(match[1]);
    const secondDatePart = Number(match[2]);
    const thirdDatePart = Number(match[3]);
    let year: number;
    let month: number;
    let day: number;

    switch (dateFormat.toUpperCase()) {
        case 'DD/MM/YYYY':
            day = firstDatePart;
            month = secondDatePart;
            year = thirdDatePart;
            break;
        case 'MM/DD/YYYY':
            month = firstDatePart;
            day = secondDatePart;
            year = thirdDatePart;
            break;
        case 'YYYY/MM/DD':
            year = firstDatePart;
            month = secondDatePart;
            day = thirdDatePart;
            break;
        default:
            return value;
    }

    let hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6] ?? 0);
    const meridiem = match[7]?.toUpperCase();

    if (meridiem) {
        if (hour < 1 || hour > 12) {
            return value;
        }

        hour = (hour % 12) + (meridiem === 'PM' ? 12 : 0);
    } else if (hour < 0 || hour > 23) {
        return value;
    }

    if (
        year < 1000 ||
        year > 9999 ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
    ) {
        return value;
    }

    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day > daysInMonth) {
        return value;
    }

    return `${padDatePart(year, 4)}-${padDatePart(month)}-${padDatePart(day)}T${padDatePart(hour)}:${padDatePart(
        minute,
    )}:${padDatePart(second)}Z`;
};

export const normalizeStormDates = (
    data: ReadonlyArray<StormPivotRow>,
    dateFormat: string,
): StormPivotRow[] => {
    if (data.length === 0) {
        return [];
    }

    const stormDateKey = getKeyByProp(data[0], StormProperties.stormDate);

    return data.map((row) => ({
        ...row,
        [stormDateKey]: normalizeStormDate(row[stormDateKey], dateFormat),
    }));
};

export const removeUnits = (input: string): string => input.replace(/\s*\(.*\)$/, '').trim();

export const getOrderedStormMeasures = (
    measures: ReadonlyArray<Flexmonster.SliceMeasure>,
): Flexmonster.SliceMeasure[] => {
    const startMeasures: Flexmonster.SliceMeasure[] = [];
    const endMeasures: Flexmonster.SliceMeasure[] = [];

    measures.forEach((measure) => {
        if (STORM_END_COLUMNS.includes(removeUnits(measure.uniqueName))) {
            endMeasures.push(measure);
        } else {
            startMeasures.push(measure);
        }
    });

    return startMeasures.concat(endMeasures);
};
